// aiToolCallStream — E (scoped A-005 extraction): pure helpers för att
// accumulera tool-calls från streamad SSE-response och konvertera till
// en lista av completed calls redo för dispatch.
//
// Extracted från AiAssistantDialog:s sendMessages-loop så logik kan
// testas isolerat. AiAssistantDialog är 1800+ rader; fullständig
// useAiSession-hook (A-005-scopet) är en separat, större refactor.
// Denna commit tar det lilla vi precis skrev (B2) + processMotorTools-
// interop och paketerar det som pure lib.

import {
  isProcessMotorTool,
  requiresApproval,
  type ProcessMotorToolName,
} from '@/lib/processMotorToolConstants';

// ToolCall-shape duplicerad lokalt så aiToolCallStream inte drar in
// processMotorTools (som har supabase-client-imports via runTrigger).
interface ToolCall {
  name: ProcessMotorToolName;
  args: Record<string, unknown>;
}

/** En chunk från SSE-stream:s delta.tool_calls. OpenAI-format: varje
 *  chunk är en partial-update av en call vid `index`. */
export interface ToolCallChunk {
  index: number;
  id?: string;
  function?: {
    name?: string;
    arguments?: string;
  };
}

/** Intern state för progressiv uppbyggnad av tool-calls. Mutabelt så
 *  caller kan återanvända mellan SSE-chunks. */
export interface ToolCallAccumulator {
  entries: Map<number, {
    id?: string;
    name?: string;
    argumentsJson: string;
  }>;
}

/** Skapa en ny tom accumulator. */
export function createToolCallAccumulator(): ToolCallAccumulator {
  return { entries: new Map() };
}

/** Applicera en chunk-array (från delta.tool_calls) på accumulatorn.
 *  OpenAI streamar tool-calls som del-updates där function.arguments är
 *  en JSON-string som byggs upp chunk-per-chunk. */
export function applyToolCallChunks(
  accumulator: ToolCallAccumulator,
  chunks: ToolCallChunk[],
): void {
  for (const chunk of chunks) {
    const existing = accumulator.entries.get(chunk.index) ?? { argumentsJson: '' };
    if (chunk.id) existing.id = chunk.id;
    if (chunk.function?.name) existing.name = chunk.function.name;
    if (chunk.function?.arguments) existing.argumentsJson += chunk.function.arguments;
    accumulator.entries.set(chunk.index, existing);
  }
}

export interface CompletedToolCall {
  id: string;
  call: ToolCall;
  needsApproval: boolean;
}

/** Konvertera accumulatorn till en array av completed tool-calls sorted
 *  by index. Filtrerar ogiltiga entries (saknar name eller inte process-
 *  motor-tool). Parse:ar argumentsJson → object med fallback till {}.
 *
 *  Returneras i samma ordning som OpenAI:s index — caller dispatch:ar
 *  i samma ordning. */
export function flushAccumulator(
  accumulator: ToolCallAccumulator,
): CompletedToolCall[] {
  const sorted = Array.from(accumulator.entries.entries())
    .sort(([a], [b]) => a - b)
    .map(([, v]) => v);

  const completed: CompletedToolCall[] = [];
  for (const entry of sorted) {
    if (!entry.name) continue;
    if (!isProcessMotorTool(entry.name)) continue;
    let args: Record<string, unknown> = {};
    try {
      args = JSON.parse(entry.argumentsJson || '{}');
    } catch {
      // Malformed arguments-JSON — dispatch:a ändå med tom args så caller
      // kan rapportera "missing-workflow_id" eller liknande.
      args = {};
    }
    const call: ToolCall = {
      name: entry.name as ToolCall['name'],
      args,
    };
    completed.push({
      id: entry.id ?? `tc-${Date.now()}-${completed.length}`,
      call,
      needsApproval: requiresApproval(call.name),
    });
  }
  return completed;
}

/** Format:era tool-result för injection som assistant-chat-message.
 *  Pure så den kan testas utan React. */
export function formatToolResultMessage(
  toolName: string,
  result: { ok: boolean; output?: unknown; error?: string },
): string {
  const icon = result.ok ? '✅' : '❌';
  const title = result.ok ? `${toolName} lyckades` : `${toolName} misslyckades`;
  const payload = result.ok ? result.output : { error: result.error };
  let bodyJson = '{}';
  try {
    bodyJson = JSON.stringify(payload, null, 2);
  } catch {
    bodyJson = String(payload);
  }
  return `${icon} **${title}**\n\`\`\`json\n${bodyJson}\n\`\`\``;
}
